import {Subscription} from "rxjs";
import {NgTemplateOutlet} from "@angular/common";
import {ActivatedRoute, Router} from "@angular/router";
import {FormControl, FormGroup, Validators} from "@angular/forms";
import {Component, inject, OnDestroy, OnInit} from '@angular/core';

import {ToastrService} from "ngx-toastr";

import {AdType} from "@numizmat/enumerations";
import {appRoutes} from "@numizmat/constants";
import {environment} from "@numizmat/environments";
import {AdApiService, AdDetailApiService} from "@numizmat/services/core";
import {AdDetailStateService} from "@numizmat/services/core/ad-detail/ad-detail-state.service";
import {DatepickerService, ImagProcessingService, MetaTagService, ScrollerService} from "@numizmat/services/common";
import {AdCardData, LoaderComponent, PageHeaderComponent, StepperComponent, StepperItem} from "@numizmat/components";

import {
  AdFormStep01Component,
  PreviewImage
} from "@numizmat/pages/ad-form-page/components/ad-form-step-01/ad-form-step-01.component";
import {AdFormStep02Component} from "@numizmat/pages/ad-form-page/components/ad-form-step-02/ad-form-step-02.component";
import {AdFormStep03Component} from "@numizmat/pages/ad-form-page/components/ad-form-step-03/ad-form-step-03.component";
import {AdFormStep04Component} from "@numizmat/pages/ad-form-page/components/ad-form-step-04/ad-form-step-04.component";

@Component({
    selector: 'nz-ad-form-page',
    imports: [
        NgTemplateOutlet,
        StepperComponent,
        PageHeaderComponent,
        AdFormStep01Component,
        AdFormStep02Component,
        AdFormStep03Component,
        AdFormStep04Component,
        LoaderComponent
    ],
    templateUrl: './ad-form-page.component.html',
    styleUrl: './ad-form-page.component.scss'
})
export class AdFormPageComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private adService: AdApiService = inject(AdApiService);
  private toastrService: ToastrService = inject(ToastrService);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private metaTagService: MetaTagService = inject(MetaTagService);
  private scrollerService: ScrollerService = inject(ScrollerService);
  private datepickerService: DatepickerService = inject(DatepickerService);
  private adDetailApiService: AdDetailApiService = inject(AdDetailApiService);
  private adDetailStateService: AdDetailStateService = inject(AdDetailStateService);
  private imagProcessingService: ImagProcessingService = inject(ImagProcessingService);

  private adId: string = '';
  private subs: Subscription = new Subscription();
  private mode: 'create' | 'update' | 'create-similar' = 'create';

  protected loadingData: boolean = true;
  protected loadingSubmit: boolean = false;
  protected selectedFormStep = 1;
  protected formSteps: StepperItem[] = [];
  protected pageTitle = '';
  protected form: FormGroup = this.initializeForm();
  protected responseErrorMessage: string = '';
  protected errorMessage = 'Не всички задължителни полета са попълнени коректно. Моля, проверете формата и опитайте отново.';

  public adSelectedPaymentOptionIds: number[] = [];
  public adSelectedDeliveryOptionIds: number[] = [];

  protected adCardData!: AdCardData;
  protected previewImages: PreviewImage[] = [];
  protected adSpecifications: { [key: number]: number } = {};
  protected isFormStep01Valid: boolean = true;
  protected isFormStep02Valid: boolean = true;
  protected isFormStep03Valid: boolean = true;

  public ngOnInit(): void {
    const path = this.activatedRoute.routeConfig?.path?.split('/')[0];
    const adId = this.activatedRoute.snapshot.paramMap.get('id') ?? '';
    if (path == appRoutes.adCrate) {
      this.mode = 'create';
      this.pageTitle = 'Добавяне на обява';
      this.loadingData = false;
      this.adDetailStateService.setPrivateAdDetails({mode: 'create'});
    } else if (path == appRoutes.adUpdate) {
      this.adId = adId;
      this.mode = 'update';
      this.pageTitle = 'Редактиране на обява';
      this.getPrivateAdDetail();
    } else if (path === appRoutes.adCrateSimilar) {
      this.adId = adId;
      this.mode = 'create-similar';
      this.pageTitle = 'Добавяне на подобна обява';
      this.getPrivateAdDetail();
    } else {
      this.router.navigate([appRoutes.root]).then();
    }
    this.initializeFormSteps();
    const subscription: Subscription = this.form.valueChanges.subscribe(() => {
      this.isFormStep01Valid = this.getForStep01().valid ? true : this.isFormStep01Valid;
      this.isFormStep02Valid = this.getForStep02().valid ? true : this.isFormStep02Valid;
      this.isFormStep03Valid = this.getForStep03().valid ? true : this.isFormStep03Valid;
    });
    this.subs.add(subscription);
    this.setMetaTags();
  }

  public ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  protected async onSubmit(): Promise<void> {
    if (!this.isFormStep01Valid || !this.isFormStep02Valid || !this.isFormStep03Valid) {
      return;
    }
    this.loadingSubmit = true;
    const request: FormData = await this.getFormData();

    if (this.mode === 'update') {
      this.adService.updateAd(this.adId, request).subscribe({
        next: response => {
          this.loadingSubmit = false;
          if (response.success) {
            this.toastrService.success(response.successMessage);
            this.router.navigate([appRoutes.root, appRoutes.ad, this.adId]).then();
          } else {
            this.responseErrorMessage = response.errorMessage;
          }
        }
      });
    } else {
      this.adService.insertAd(request).subscribe({
        next: response => {
          this.loadingSubmit = false;
          if (response.success) {
            this.toastrService.success(response.successMessage);
            this.router.navigate([appRoutes.root, appRoutes.ad, response.adId]).then();
          } else {
            this.responseErrorMessage = response.errorMessage;
          }
        }
      });
    }
  }

  protected getForStep01(): FormGroup {
    return this.form.get("step01") as FormGroup;
  }

  protected getForStep02(): FormGroup {
    return this.form.get("step02") as FormGroup;
  }

  protected getForStep03(): FormGroup {
    return this.form.get("step03") as FormGroup;
  }

  protected onPreviewImagesChange(images: PreviewImage[]): void {
    this.previewImages = images;
  }

  protected onChangeAdSpecifications(specifications: { [key: number]: number }) {
    this.adSpecifications = specifications;
  }

  protected onAdSelectedPaymentOptionIdsChange(paymentOptionIds: number[]): void {
    this.adSelectedPaymentOptionIds = paymentOptionIds;
  }

  protected onAdSelectedDeliveryOptionIdsChange(deliveryOptionIds: number[]): void {
    this.adSelectedDeliveryOptionIds = deliveryOptionIds;
  }

  protected onSelectFormStep(stepId: number): void {
    if (this.selectedFormStep === stepId) {
      return;
    }
    const stepIndex = this.formSteps.findIndex(x => x.id === stepId);
    if (this.selectedFormStep === 1) {
      const forStep01 = this.getForStep01();
      forStep01.markAllAsTouched();
      this.isFormStep01Valid = forStep01.disabled || forStep01.valid;
      if (this.isFormStep01Valid || !this.formSteps[stepIndex].disabled) {
        this.formSteps[stepIndex].disabled = false;
      } else {
        return;
      }
    } else if (this.selectedFormStep === 2) {
      const forStep02 = this.getForStep02();
      forStep02.updateValueAndValidity();
      forStep02.markAllAsTouched();
      this.isFormStep02Valid = forStep02.disabled || forStep02.valid;
      if (this.isFormStep02Valid || !this.formSteps[stepIndex].disabled) {
        this.formSteps[stepIndex].disabled = false;
      } else {
        return;
      }
    } else if (this.selectedFormStep === 3) {
      const forStep03 = this.getForStep03();
      forStep03.markAllAsTouched();
      this.isFormStep03Valid = forStep03.disabled || forStep03.valid;
      if (this.isFormStep03Valid || !this.formSteps[stepIndex].disabled) {
        this.formSteps[stepIndex].disabled = false;
      } else {
        return;
      }
    }
    this.goToFormStep(stepId);
  }

  private goToFormStep(stepId: number): void {
    if (stepId === 4 && this.isFormStep01Valid && this.isFormStep02Valid && this.isFormStep03Valid) {
      const forStep01 = this.getForStep01().getRawValue();
      const forStep02 = this.getForStep02().getRawValue();
      this.adCardData = {
        adId: '',
        title: forStep01.title,
        imageUrl: this.previewImages[0].imageUrl,
        price: forStep02.adType === AdType.DirectSale
          ? forStep02.fixedPrice
          : forStep02.startingPrice,
        adType: forStep02.adType,
        expiryDate: this.datepickerService.parseNgbDateStructToDate(forStep02.endDate),
        additionalImageIds: this.previewImages.map(() => {
          return ''
        }),
      };
    }
    this.selectedFormStep = stepId;
    this.scrollerService.scrollToTop();
  }

  private getPrivateAdDetail(): void {
    this.adDetailApiService.getPrivateAdDetails(this.adId).subscribe({
      next: response => {
        this.loadingData = false;
        this.adDetailStateService.setPrivateAdDetails({
          mode: this.mode,
          adId: this.adId,
          data: response
        });
      }
    });
  }

  private initializeForm(): FormGroup {
    return new FormGroup({
      step01: new FormGroup({
        title: new FormControl(null, [Validators.required]),
        identifier: new FormControl(null, [Validators.maxLength(20)]),
        description: new FormControl(null, [Validators.maxLength(2000)]),
        adCategoryId: new FormControl(null, [Validators.required]),
        adSubcategoryId: new FormControl({value: null, disabled: true}, [Validators.required]),
        imagesCount: new FormControl(0, [Validators.min(1), Validators.max(10)]),
      }),
      step02: new FormGroup({
        adType: new FormControl(AdType.DirectSale, [Validators.required]),
        fixedPrice: new FormControl(null, [Validators.required]),
        isOfferSubmissionAllowed: new FormControl(false, [Validators.required]),
        startingPrice: new FormControl({value: null, disabled: true}, [Validators.required]),
        bidIncrement: new FormControl({value: null, disabled: true}, [Validators.required]),
        hasBuyNowPrice: new FormControl(false),
        buyNowPrice: new FormControl({value: null, disabled: true}, [Validators.required]),
        hasReservedPrice: new FormControl(false),
        reservedPrice: new FormControl({value: null, disabled: true}, [Validators.required]),
        hasAdBidExtensionTimeOptionId: new FormControl(false),
        adBidExtensionTimeOptionId: new FormControl({value: null, disabled: true}, [Validators.required]),
        hasAdAutoRenewAfterExpiredId: new FormControl(false),
        adAutoRenewAfterExpiredId: new FormControl({value: null, disabled: true}, [Validators.required]),
        startDate: new FormControl(null, [Validators.required]),
        startHour: new FormControl(null, [Validators.required]),
        startMinutes: new FormControl(null, [Validators.required]),
        endDate: new FormControl(null, [Validators.required]),
        endHour: new FormControl(null, [Validators.required]),
        endMinutes: new FormControl(null, [Validators.required])
      }),
      step03: new FormGroup({
        regionId: new FormControl(null, [Validators.required]),
        municipalityId: new FormControl({value: null, disabled: true}, [Validators.required]),
        populatedPlaceId: new FormControl({value: null, disabled: true}, [Validators.required]),
        isUserDefaultAddress: new FormControl(false),
        additionalAdInfo: new FormControl(null, [Validators.maxLength(1000)]),
        paymentAndDeliveryInfo: new FormControl(null, [Validators.maxLength(1000)]),
      })
    });
  }

  private initializeFormSteps(): void {
    this.formSteps = Array.from({length: 4}, (_, index) => ({
      id: index + 1,
      disabled: this.mode === 'create' && index > 0,
    }));
  }

  private async getFormData(): Promise<FormData> {
    const formData = new FormData();
    const forStep01 = this.getForStep01().getRawValue();
    const forStep02 = this.getForStep02().getRawValue();
    const forStep03 = this.getForStep03().getRawValue();

    Object.entries(forStep01).forEach(([key, value]) => {
      if (!!value) {
        formData.append(key, value as string);
      }
    });

    Object.keys(this.adSpecifications).forEach(key => {
      if (!!this.adSpecifications[+key]) {
        formData.append(`adSelectedSpecificationOptionIds`, this.adSpecifications[+key].toString());
      }
    });

    if (this.mode === 'update') {
      for (const [index, image] of this.previewImages.entries()) {
        if (image.imageId) {
          formData.append(`images[${index}].file`, '');
          formData.append(`images[${index}].adImageId`, image.imageId);
        } else {
          const resizedImage = (await this.resizeAllImages([image], 2000))[0];
          formData.append(`images[${index}].file`, resizedImage);
          formData.append(`images[${index}].adImageId`, '');
        }
      }
    } else {
      const resizedImages = await this.resizeAllImages(this.previewImages, 2000);
      resizedImages.forEach((image) => {
        formData.append('images', image);
      });
    }

    Object.entries(forStep02).forEach(([key, value]) => {
      if (!!value &&
        key !== 'startDate' &&
        key !== 'startHour' &&
        key !== 'startMinutes' &&
        key !== 'endDate' &&
        key !== 'endHour' &&
        key !== 'endMinutes') {
        formData.append(key, value as string);
      }
    });

    const startDate = this.datepickerService.parseNgbDateStructToIsoFormat(forStep02.startDate, forStep02.startHour, forStep02.startMinutes);
    const endDate = this.datepickerService.parseNgbDateStructToIsoFormat(forStep02.endDate, forStep02.endHour, forStep02.endMinutes);

    formData.append('startDateTime', startDate);
    formData.append('endDateTime', endDate);

    Object.entries(forStep03).forEach(([key, value]) => {
      if (!!value) {
        formData.append(key, value as string);
      }
    });

    this.adSelectedPaymentOptionIds.forEach((optionId) => {
      formData.append('adSelectedPaymentOptionIds', optionId.toString());
    });

    this.adSelectedDeliveryOptionIds.forEach((optionId) => {
      formData.append(`adSelectedDeliveryOptionIds`, optionId.toString());
    });

    return formData;
  }

  private async resizeAllImages(images: PreviewImage[], maxSize: number): Promise<Blob[]> {
    const resizedImagesPromises = images.map(async (image) => {
      if (image.file) {
        return await this.imagProcessingService.resizeImage(image.file, maxSize);
      }
      return null;
    });
    const resizedBlobs: (Blob | null)[] = await Promise.all(resizedImagesPromises);
    return resizedBlobs.filter((blob): blob is Blob => blob !== null);
  }

  private setMetaTags(): void {
    if (this.mode === 'create') {
      this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.adCrate}`);
      this.metaTagService.setTitle('Добавяне на обява | Collectoria');
      this.metaTagService.setDescription('Добавяне на обява | Collectoria');
    } else if (this.mode === 'update') {
      this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.adUpdate}/${this.adId}`);
      this.metaTagService.setTitle('Редактиране на обява | Collectoria');
      this.metaTagService.setDescription('Редактиране на обява | Collectoria');
    } else if (this.mode === 'create-similar') {
      this.metaTagService.setCanonicalUrl(`${environment.applicationDomain}/${appRoutes.adCrateSimilar}/${this.adId}`);
      this.metaTagService.setTitle('Добавяне на подобна обява | Collectoria');
      this.metaTagService.setDescription('Добавяне на подобна обява | Collectoria');
    }
  }
}
